import { mutate } from "swr"

import useResource from "./useResource"
import API from "./api"

export default function useViewable({ id, type }) {
  const viewableType = type === "group" ? "view_groups" : "views"
  const viewableKey = id ? `${viewableType}/${id}` : null
  const [viewable] = useResource({ key: viewableKey })

  async function reset() {
    await API.put(`/${viewableType}/${id}/reset`)
    return mutate(viewableKey)
  }

  function saveAs(viewableId) {
    return async (name) => {
      const data = await API.put(`/${viewableType}/${viewableId}/save_as`, {
        name,
      })
      await Promise.all([
        mutate(viewableKey),
        mutate(`views/${data.id}`, data, false),
      ])
      return data
    }
  }

  function saveChanges(viewableId) {
    return async () => {
      await API.put(`/${viewableType}/${viewableId}/save_changes`)
      return mutate(viewableKey)
    }
  }

  function updateComparison(viewableId) {
    return async (data) => {
      await API.put(`/${viewableType}/${viewableId}/comparison`, data)
      return mutate(viewableKey)
    }
  }

  async function applyTemplate(templateId) {
    await API.put(`${viewableType}/${id}/templates/${templateId}`)
    return mutate(viewableKey)
  }

  async function addField(data) {
    const key = `${viewableType}/${id}/fields`
    const field = await API.post(key, data)

    if (typeof field === "object") {
      field.destroy = destroyField(field.id)
    }

    await mutate(viewableKey)
    return field
  }

  function destroyField(viewFieldId) {
    const key = `${viewableType}/${id}/fields/${viewFieldId}`
    return async () => {
      await API.delete(key)
      return mutate(viewableKey)
    }
  }

  async function updateColumnWidth(viewFieldId, columnWidth) {
    const viewField = viewable.fields.find((vf) => vf.id === viewFieldId)
    viewField.column_width = columnWidth

    viewable.updateLocal(viewable)
    await API.put(`/${viewableType}/${id}/fields/${viewFieldId}`, {
      column_width: columnWidth,
    })
    return mutate(viewableKey)
  }

  async function reorderFields(from, to) {
    await API.post(`/${viewableType}/${id}/reorder_fields`, { from, to })
    return mutate(viewableKey)
  }

  async function changeFieldPresentation(fieldId, presentation) {
    await API.put(`/${viewableType}/${id}/fields/${fieldId}`, {
      presentation,
      id: fieldId,
    })
    return mutate(viewableKey)
  }

  async function addCondition(data) {
    const key = `${viewableType}/${id}/conditions`
    const condition = await API.post(key, data)

    if (typeof condition === "object") {
      condition.update = updateCondition(condition.id)
      condition.destroy = destroyCondition(condition.id)
    }

    mutate(viewableKey)
    return condition
  }

  function updateCondition(conditionId) {
    const key = `${viewableType}/${id}/conditions/${conditionId}`
    return async (data) => {
      await API.put(key, data)
      await mutate(viewableKey)
      return viewable.refresh() // Not sure we need both here?
    }
  }

  function destroyCondition(conditionId) {
    const key = `${viewableType}/${id}/conditions/${conditionId}`
    return async () => {
      await API.delete(key)
      return mutate(viewableKey)
    }
  }

  async function updateTable(data, optionsParam) {
    const options = { optimistic: false, ...optionsParam }

    const update = API.put(`/${viewableType}/${id}/table`, data)

    if (options.optimistic) {
      return mutate(
        viewableKey,
        { ...viewable, table: { ...viewable.table, ...data } },
        false
      )
    } else {
      await update
      return mutate(viewableKey)
    }
  }

  async function freezeField(fieldId) {
    await API.put(`/${viewableType}/${id}/frozen_field/`, { field_id: fieldId })
    return mutate(viewableKey)
  }

  async function unfreezeField() {
    await API.delete(`/${viewableType}/${id}/frozen_field`)
    return mutate(viewableKey)
  }

  function userCan(action) {
    return viewable.user_permissions[action]
  }

  if (viewable) {
    viewable.type = type

    viewable.fields.reorder = reorderFields
    viewable.fields.add = addField
    viewable.fields.changeFieldPresentation = changeFieldPresentation
    viewable.fields.updateColumnWidth = updateColumnWidth

    viewable.conditions.add = addCondition

    viewable.reset = reset
    viewable.saveAs = saveAs(viewable.id)
    viewable.saveChanges = saveChanges(viewable.id)

    viewable.updateComparison = updateComparison(viewable.id)
    viewable.updateTable = updateTable
    viewable.freezeField = freezeField
    viewable.unfreezeField = unfreezeField

    viewable.applyTemplate = applyTemplate

    for (const condition of viewable.conditions) {
      condition.update = updateCondition(condition.id)
      condition.destroy = destroyCondition(condition.id)
      condition.refresh = () => mutate(viewableKey)
    }

    for (const condition of viewable.sticky_conditions || []) {
      condition.update = updateCondition(condition.id)
      condition.destroy = destroyCondition(condition.id)
      condition.refresh = () => mutate(viewableKey)
    }

    for (const field of viewable.fields) {
      field.destroy = destroyField(field.id)
    }

    viewable.userCan = userCan
    viewable.swrKey = viewableKey
  }

  return viewable
}
